class DetailsModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.closeButton = this.querySelector('button[type="button"]');

    this.bodyScrollLocked = false;
    this.headerSearchBreakpoint = window.matchMedia('(max-width: 989px)');

    this.detailsContainer?.addEventListener(
      'keyup',
      (event) => event.code?.toUpperCase() === 'ESCAPE' && this.close()
    );

    this.summaryToggle?.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton?.addEventListener('click', this.close.bind(this));

    this.summaryToggle?.setAttribute('role', 'button');

    /*
      A busca da header pode permanecer aberta enquanto a página rola
      no desktop. Em telas menores, o bloqueio de rolagem é mantido.
    */
    this.onHeaderSearchBreakpointChange = this.onHeaderSearchBreakpointChange.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);

    this.headerSearchBreakpoint.addEventListener?.('change', this.onHeaderSearchBreakpointChange);
    window.addEventListener('resize', this.onWindowResize, { passive: true });
  }

  disconnectedCallback() {
    this.headerSearchBreakpoint.removeEventListener?.('change', this.onHeaderSearchBreakpointChange);
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('scroll', this.onWindowScroll);
  }

  isOpen() {
    return Boolean(this.detailsContainer?.hasAttribute('open'));
  }

  isHeaderSearch() {
    return this.matches('details-modal.vf-header-search') || this.classList.contains('vf-header-search');
  }

  shouldLockBodyScroll() {
    /*
      Outros modais continuam usando o comportamento original do Dawn.
      Apenas a busca da header libera a rolagem no desktop.
    */
    if (!this.isHeaderSearch()) return true;

    return this.headerSearchBreakpoint.matches;
  }

  getAnnouncementElement() {
    return (
      document.querySelector('.announcement-bar-section .utility-bar') ||
      document.querySelector('[data-vf-announcement-bar]') ||
      document.querySelector('.vf-announcement')
    );
  }

  getAnnouncementText(announcement) {
    const visibleMessage = Array.from(
      announcement?.querySelectorAll('.announcement-bar__message') || []
    ).find((message) => {
      const slide = message.closest('[aria-hidden]');
      return !slide || slide.getAttribute('aria-hidden') !== 'true';
    });

    const message =
      visibleMessage ||
      announcement?.querySelector('.announcement-bar__message') ||
      announcement?.querySelector('[role="region"]') ||
      announcement;

    return message?.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  ensureSearchAnnouncementMirror(searchModal) {
    let mirror = searchModal.querySelector('.vf-search-announcement-mirror');

    if (!mirror) {
      mirror = document.createElement('div');
      mirror.className = 'vf-search-announcement-mirror';
      mirror.setAttribute('aria-hidden', 'true');
      searchModal.prepend(mirror);
    }

    return mirror;
  }

  updateHeaderSearchOffset() {
    if (!this.isHeaderSearch()) return;

    const searchModal = this.detailsContainer?.querySelector('.search-modal');
    if (!searchModal) return;

    const announcement = this.getAnnouncementElement();
    const fallbackHeight = this.headerSearchBreakpoint.matches ? 34 : 38;
    const announcementRect = announcement?.getBoundingClientRect();
    const measuredHeight = announcement
      ? Math.ceil(
          Math.max(
            announcementRect?.height || 0,
            announcement.offsetHeight || 0
          )
        )
      : 0;

    const announcementHeight = measuredHeight > 0 ? measuredHeight : fallbackHeight;
    const announcementText =
      this.getAnnouncementText(announcement) ||
      'SUMMER COLLECTION IS OUT NOW - FREE DELIVERY ON ORDERS OVER £40';

    /*
      Quando a barra real ainda está visível no viewport, a busca começa
      logo abaixo dela e nenhuma cópia é exibida.

      Depois que a barra real sai da tela, a busca encosta no topo e mostra
      uma faixa própria, sem mover nem duplicar a seção original.
    */
    const realAnnouncementIsVisible = Boolean(
      announcementRect &&
      announcementRect.height > 0 &&
      announcementRect.bottom > 0 &&
      announcementRect.top < window.innerHeight
    );

    const mirror = this.ensureSearchAnnouncementMirror(searchModal);
    mirror.textContent = `•  ${announcementText}  •`;

    searchModal.style.setProperty(
      '--vf-search-modal-top',
      `${realAnnouncementIsVisible ? Math.max(0, Math.ceil(announcementRect.bottom)) : 0}px`
    );
    searchModal.style.setProperty(
      '--vf-search-announcement-height',
      `${announcementHeight}px`
    );

    searchModal.classList.toggle(
      'vf-search-uses-real-announcement',
      realAnnouncementIsVisible
    );
    searchModal.classList.toggle(
      'vf-search-uses-announcement-mirror',
      !realAnnouncementIsVisible
    );
  }

  onWindowScroll() {
    if (!this.isOpen() || !this.isHeaderSearch()) return;
    this.updateHeaderSearchOffset();
  }

  onWindowResize() {
    if (!this.isOpen() || !this.isHeaderSearch()) return;
    this.updateHeaderSearchOffset();
  }

  onHeaderSearchBreakpointChange() {
    if (!this.isOpen() || !this.isHeaderSearch()) return;

    this.updateHeaderSearchOffset();

    if (this.shouldLockBodyScroll()) {
      this.lockBodyScroll();
    } else {
      this.unlockBodyScroll();
    }
  }

  setHeaderSearchOpenState(isOpen) {
    if (!this.isHeaderSearch()) return;

    document.documentElement.classList.toggle('vf-header-search-is-open', isOpen);
    document.body?.classList.toggle('vf-header-search-is-open', isOpen);

    if (isOpen) {
      this.updateHeaderSearchOffset();
    }
  }

  lockBodyScroll() {
    if (this.bodyScrollLocked) return;

    document.body.classList.add('overflow-hidden');
    this.bodyScrollLocked = true;
  }

  unlockBodyScroll() {
    if (!this.bodyScrollLocked) return;

    this.bodyScrollLocked = false;

    /*
      Não remova overflow-hidden se outro details-modal aberto ainda
      depender do bloqueio de rolagem.
    */
    const anotherLockedModalIsOpen = Array.from(document.querySelectorAll('details-modal')).some((modal) => {
      if (modal === this) return false;

      const modalIsOpen =
        (typeof modal.isOpen === 'function' && modal.isOpen()) ||
        Boolean(modal.querySelector('details[open]'));

      return modalIsOpen && modal.bodyScrollLocked === true;
    });

    if (!anotherLockedModalIsOpen) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  onSummaryClick(event) {
    event.preventDefault();

    const details = event.target.closest('details') || this.detailsContainer;

    details?.hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
      this.close(false);
    }
  }

  open(event) {
    if (!this.detailsContainer) return;

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    this.setHeaderSearchOpenState(true);

    if (this.isHeaderSearch()) {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      this.updateHeaderSearchOffset();
    }

    if (this.shouldLockBodyScroll()) {
      this.lockBodyScroll();
    } else {
      this.unlockBodyScroll();
    }

    const focusContainer = this.detailsContainer.querySelector('[tabindex="-1"]');
    const initialFocus = this.detailsContainer.querySelector('input:not([type="hidden"])');

    if (focusContainer && initialFocus) {
      trapFocus(focusContainer, initialFocus);
    }
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);

    this.detailsContainer?.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);

    this.unlockBodyScroll();

    if (this.isHeaderSearch()) {
      window.removeEventListener('scroll', this.onWindowScroll);

      const searchModal = this.detailsContainer?.querySelector('.search-modal');
      searchModal?.classList.remove(
        'vf-search-uses-real-announcement',
        'vf-search-uses-announcement-mirror'
      );

      const anotherHeaderSearchIsOpen = Array.from(
        document.querySelectorAll('details-modal.vf-header-search')
      ).some((modal) => {
        if (modal === this) return false;

        return (
          (typeof modal.isOpen === 'function' && modal.isOpen()) ||
          Boolean(modal.querySelector('details[open]'))
        );
      });

      if (!anotherHeaderSearchIsOpen) {
        this.setHeaderSearchOpenState(false);
      }
    }
  }
}

customElements.define('details-modal', DetailsModal);


/* ============================================================
   VF / Pebble & Wick — Cinematic Details Modal Enhancements
   Safe layer on top of Dawn details-modal.js.
   Keeps focus trap, ESC close, overlay click and modal accessibility intact.
   ============================================================ */

(() => {
  const DetailsModalElement = customElements.get('details-modal');
  if (!DetailsModalElement || DetailsModalElement.prototype.__vfCinematicPatched) return;

  DetailsModalElement.prototype.__vfCinematicPatched = true;

  const originalOpen = DetailsModalElement.prototype.open;
  const originalClose = DetailsModalElement.prototype.close;
  const originalOnSummaryClick = DetailsModalElement.prototype.onSummaryClick;

  const root = document.documentElement;

  const setModalState = (modal, state, detail = {}) => {
    modal.dataset.vfModalState = state;

    modal.classList.toggle('vf-details-modal-opening', state === 'opening');
    modal.classList.toggle('vf-details-modal-open', state === 'open');
    modal.classList.toggle('vf-details-modal-closing', state === 'closing');

    root.classList.toggle('vf-details-modal-is-open', state === 'open' || state === 'opening');
    document.body?.classList.toggle('vf-details-modal-is-open', state === 'open' || state === 'opening');

    modal.dispatchEvent(
      new CustomEvent(`vf:details-modal-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const ensureModalAccessibility = (modal) => {
    const dialog =
      modal.detailsContainer?.querySelector('[role="dialog"]') ||
      modal.detailsContainer?.querySelector('[tabindex="-1"]') ||
      modal.detailsContainer;

    if (dialog && !dialog.getAttribute('aria-modal')) {
      dialog.setAttribute('aria-modal', 'true');
    }

    if (modal.summaryToggle) {
      modal.summaryToggle.setAttribute('aria-expanded', modal.isOpen() ? 'true' : 'false');
      modal.summaryToggle.setAttribute('aria-haspopup', 'dialog');
    }
  };

  DetailsModalElement.prototype.onSummaryClick = function vfOnSummaryClick(event) {
    this.classList.add('vf-details-modal-triggered');

    window.setTimeout(() => {
      this.classList.remove('vf-details-modal-triggered');
    }, 420);

    return originalOnSummaryClick.call(this, event);
  };

  DetailsModalElement.prototype.open = function vfOpen(event) {
    ensureModalAccessibility(this);
    setModalState(this, 'opening', {
      trigger: event?.target || this.summaryToggle
    });

    const result = originalOpen.call(this, event);

    window.requestAnimationFrame(() => {
      ensureModalAccessibility(this);
      this.summaryToggle?.setAttribute('aria-expanded', 'true');
      setModalState(this, 'open', {
        trigger: event?.target || this.summaryToggle
      });
    });

    return result;
  };

  DetailsModalElement.prototype.close = function vfClose(focusToggle = true) {
    if (!this.isOpen?.()) return;

    setModalState(this, 'closing', {
      focusToggle
    });

    const result = originalClose.call(this, focusToggle);

    window.setTimeout(() => {
      this.summaryToggle?.setAttribute('aria-expanded', 'false');
      setModalState(this, 'closed', {
        focusToggle
      });
    }, 220);

    return result;
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      document.querySelectorAll('details-modal').forEach((modal) => {
        modal.classList.add('vf-details-modal-enhanced');
        ensureModalAccessibility(modal);
      });
    },
    { once: true }
  );

  document.addEventListener('keyup', (event) => {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;

    document.querySelectorAll('details-modal').forEach((modal) => {
      if (!modal.isOpen?.()) return;

      modal.classList.add('vf-details-modal-escape');
      window.setTimeout(() => modal.classList.remove('vf-details-modal-escape'), 360);
    });
  });
})();
